import React from 'react';
import { Box, Container, Typography, Button, styled } from '@mui/material';

const COMPANY_DASHBOARD_IMG = `${process.env.PUBLIC_URL}/assets/Companydashboard.jpg`;

const Section = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: '124px 0 72px',
    minHeight: 'auto',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background:
        'linear-gradient(165deg, #F8FAFF 0%, #FFFFFF 32%, #F4F7FF 70%, #EEF2FF 100%)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
            'radial-gradient(ellipse 55% 45% at 15% 20%, rgba(0, 102, 255, 0.09) 0%, transparent 65%), radial-gradient(circle at 85% 15%, rgba(99, 102, 241, 0.08) 0%, transparent 45%)',
        pointerEvents: 'none',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-18%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(140%, 1600px)',
        height: '55%',
        background:
            'radial-gradient(ellipse at center, rgba(0, 82, 204, 0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    '@media (max-width: 968px)': {
        minHeight: 'auto',
        padding: '116px 0 64px',
    },
    '@media (max-width: 600px)': {
        padding: '104px 0 52px',
    },
    [theme.breakpoints.up('lg')]: {
        paddingBottom: theme.spacing(7),
    },
}));

const Grid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.12fr) minmax(0, 0.88fr)',
    gap: '64px',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    '@media (max-width: 968px)': {
        gridTemplateColumns: '1fr',
        gap: '48px',
    },
});

const Kicker = styled(Typography)(({ theme }) => ({
    display: 'inline-block',
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0066FF',
    backgroundColor: 'rgba(0, 102, 255, 0.1)',
    padding: theme.spacing(0.75, 1.75),
    borderRadius: 999,
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.up('sm')]: {
        fontSize: '0.75rem',
        padding: theme.spacing(0.75, 2.25),
    },
}));

const Title = styled(Typography)({
    fontSize: 'clamp(2.25rem, 4vw, 3.65rem)',
    fontWeight: 700,
    lineHeight: 1.08,
    letterSpacing: '-0.03em',
    color: '#0F172A',
    marginBottom: '28px',
    maxWidth: '760px',
    '@media (max-width: 968px)': {
        maxWidth: '100%',
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
});

const TitleAccent = styled('span')({
    background: 'linear-gradient(120deg, #0066FF 0%, #4338CA 55%, #6366F1 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
});

const Body = styled(Box)({
    maxWidth: '640px',
    '@media (max-width: 968px)': {
        maxWidth: '100%',
        textAlign: 'center',
        margin: '0 auto',
    },
});

const Lead = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    lineHeight: 1.75,
    color: 'rgba(15, 23, 42, 0.82)',
    fontWeight: 400,
    marginBottom: '24px',
    textAlign: 'justify',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.0625rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '1.125rem',
    },
    '@media (max-width: 968px)': {
        textAlign: 'center',
    },
}));

const Closing = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    lineHeight: 1.75,
    color: 'rgba(15, 23, 42, 0.82)',
    fontWeight: 400,
    textAlign: 'justify',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.0625rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '1.125rem',
    },
    '@media (max-width: 968px)': {
        textAlign: 'center',
    },
}));

// const CtaRow = styled(Box)({
//     display: 'flex',
//     flexWrap: 'wrap',
//     gap: '14px',
//     marginTop: '36px',
//     '@media (max-width: 968px)': {
//         justifyContent: 'center',
//     },
// });

// const PrimaryButton = styled(Button)({
//     padding: '15px 28px',
//     fontSize: '15px',
//     fontWeight: 600,
//     borderRadius: '12px',
//     textTransform: 'none',
//     background: 'linear-gradient(135deg, #0066FF, #0052CC)',
//     color: '#FFFFFF',
//     minWidth: '180px',
//     boxShadow: '0 4px 22px rgba(0, 102, 255, 0.32)',
//     '&:hover': {
//         background: 'linear-gradient(135deg, #0052CC, #003D99)',
//         transform: 'translateY(-2px)',
//         boxShadow: '0 12px 30px rgba(0, 102, 255, 0.38)',
//     },
// });

// const SecondaryButton = styled(Button)({
//     padding: '15px 28px',
//     fontSize: '15px',
//     fontWeight: 600,
//     borderRadius: '12px',
//     textTransform: 'none',
//     color: '#334155',
//     borderColor: 'rgba(148, 163, 184, 0.55)',
//     borderWidth: '1.5px',
//     background: 'rgba(255, 255, 255, 0.7)',
//     backdropFilter: 'blur(8px)',
//     '&:hover': {
//         borderColor: '#0066FF',
//         color: '#0066FF',
//         background: 'rgba(0, 102, 255, 0.04)',
//     },
// });

const VisualCol = styled(Box)({
    position: 'relative',
    minHeight: '340px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 968px)': {
        minHeight: '280px',
        maxWidth: '480px',
        margin: '0 auto',
        width: '100%',
    },
});

const CompanyHero = () => {
    return (
        <Section component="main" aria-labelledby="company-hero-title">
            <Container maxWidth="xl">
                <Grid>
                    <Box>
                        <Kicker component="p">Company</Kicker>
                        <Title variant="h1" id="company-hero-title">
                            Nobody built software for how home healthcare operations run.{' '}
                            <TitleAccent>So we did.</TitleAccent>
                        </Title>
                        <Body>
                            <Lead component="p">
                                Clinic software was built for clinics. It handles what happens
                                inside a facility. It was never designed for a team managing
                                therapists across home visits and in-house sessions simultaneously
                                - verifying work that happens outside the building, enforcing
                                documentation before someone leaves a patient's home,
                                calculating payouts from confirmed records rather than
                                self-reported ones.
                                <br />
                                That gap existed for years. meDZhub was built to close it.
                            </Lead>
                            <Closing component="p">
                                
                            </Closing>
                        </Body>
                    </Box>

                    <VisualCol>
                        <Box
                            component="img"
                            src={COMPANY_DASHBOARD_IMG}
                            alt="meDZhub company operations dashboard"
                            loading="eager"
                            decoding="async"
                            sx={{
                                width: '100%',
                                maxWidth: { xs: 420, md: 520 },
                                height: 'auto',
                                display: 'block',
                                borderRadius: '22px',
                                boxShadow:
                                    '0 24px 55px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(226, 232, 240, 0.9) inset',
                                border: '1px solid rgba(226, 232, 240, 0.85)',
                            }}
                        />
                    </VisualCol>
                </Grid>
            </Container>
        </Section>
    );
};

export default CompanyHero;
